import { type AdminSelectQuery, type DistrictAdminSchoolsQuery } from "@crowdcoursing/api/graphql";
import { type IterableElement } from "type-fest";

import { type SchoolMembers } from "../../organisms/DataGrid";
import { type AdminTemplate } from "../../templates/AdminTemplate/AdminTemplate";

export type School = IterableElement<AdminSelectQuery["mySchools"]>;

export type District = IterableElement<AdminSelectQuery["mySchoolDistricts"]>;

export const round = (value: number, precision: number) => {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
};

export const getNextGoal = (current: number) => {
  if (current < 10) {
    return "10";
  }
  if (current < 25) {
    return "25";
  }
  if (current < 50) {
    return "50";
  }
  if (current < 100) {
    return "100";
  }
  if (current < 500) {
    return "500";
  }
  if (current < 1000) {
    return "1,000";
  }
  if (current < 5000) {
    return "5,000";
  }
  if (current < 10000) {
    return "10,000";
  }
  if (current < 15000) {
    return "15,000";
  }
};

function toHoursAndMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  // Format hours with commas for thousands if needed
  const formattedHours = hours.toLocaleString("en-US");
  return `${formattedHours}h ${minutes}m`;
}

function numberToDollarsAndCents(amount: number): string {
  return amount.toLocaleString("en-US", {
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: "currency",
  });
}

export const formatStat = ([key, value]: [string, number]) => {
  if (key.startsWith("minutes") && typeof value === "number") {
    return {
      [key]: toHoursAndMinutes(value),
    };
  }
  if (key.startsWith("economic") && typeof value === "number") {
    return {
      [key]: numberToDollarsAndCents(value),
    };
  }

  return { [key]: String(value) };
};

export function formatToMoney(amount: number, currency = "USD", locale = "en-US") {
  if (typeof amount !== "number") {
    throw new Error("Input must be a number");
  }

  return new Intl.NumberFormat(locale, {
    currency,
    style: "currency",
  }).format(amount);
}

export function formatToTwoDecimalPlaces(value: number | undefined): string {
  if (value === undefined) {
    return "0.00";
  }
  return value.toFixed(2);
}

export const formatMinuteStat = (value: number | undefined) => {
  if (value === undefined) {
    return toHoursAndMinutes(0);
  }
  return toHoursAndMinutes(value);
};

export const mapToSchoolMembersData = (
  schools: NonNullable<DistrictAdminSchoolsQuery["schoolDistrict"]>["schools"],
): NonNullable<React.ComponentProps<typeof SchoolMembers>["data"]> =>
  schools.map(({ id, bubbleId, memberCount: membersCount, requestCount: requestsCount, name }) => ({
    bubbleId,
    id,
    membersCount,
    name,
    requestsCount,
  }));

export const isDistrict = (value: District | School): value is District =>
  (value as District).schoolDistrictId !== undefined;

export const getAdminType = (value?: District | School): React.ComponentProps<typeof AdminTemplate>["type"] => {
  if (!value) {
    return "unknown";
  }

  if (isDistrict(value)) {
    return "district";
  }

  return "school";
};

/**
 * Roles shown in Add User (school/district) after `useBubbleGetRoles` returns.
 * Only these strings are kept; everything else from the API is hidden in that modal.
 */
export const BUBBLE_ADD_USER_ROLE_ALLOWLIST = [
  "Admin",
  "Educator",
  "Front Office",
  "Principal",
  "Student",
  "Tutor",
  "Volunteer",
] as const;
